import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'utils/cops-moment';
import { blockTypes } from '../../../../../utils/app-configs';
import { ItemTypes } from '../../../config';
import { dayViews, viewIds } from '../../../config/view';
import { setAdjacentBlock } from '../../../containers/gantt-actions';
import getItemBlockDimensions from '../../../engine/block-dimensions';
import Draggable from '../../dnd/components/dnd-draggable';
import Block from './block';

const Wrapper = styled.div`
  position: relative;
`;

const ItemBlockWrapper = styled.div`
  position: absolute;
  width: ${p => p.width ? p.width : 0}%;
  left: calc(${p => p.left ? p.left : 0}% + ${p =>
    p.day * p.widthDayBreaker
  }px + ${p => p.isNextWeek ? p.nextWeekOffset : 0}% + ${p =>
    p.isNextWeek ? 5 * p.widthDayBreaker : 0}px);
  top: ${p => p.top ? p.top : 0}px;
`;

const AdjacentArea = styled.div`
    background-color: transparent;
    position: absolute;
    right: ${p => p.right ? `${p.right}px` : 0};
    width: ${p => p.width}px;
    height: 100%;
    z-index: 1;
`;

const BlockWrapper = ({
  block,
  top,
  ganttDimensions,
  viewDimensions,
  dateReference,
  view,
  publishToSAP,
  splitBlock,
  markDateConfirmed,
  isDoingAction,
  isDraggingOver,
  deletePlanning,
  fetchBlockData,
  fetchBlockDataSuccess,
  updateBlockId,
  updateBlockOperationInfo,
  markDateUnconfirmed,
  restoreBackup,
  recalculateTravelTime,
  planningAndViewToOpen,
}) => {
  const dispatch = useDispatch();
  // one block can be divided into several itemBlocks, depending on
  // the amount of days one block covers, we then have one itemBlock per day
  const [itemBlocks, setItemBlocks] = useState([]);
  const [calendarDay, setCalendarDay] = useState(null);
  const { adjacentBlock } = useSelector(state => state.gantt);
  const { item } = useDragLayer(monitor => ({
    item: monitor.getItem(),
  }));
  const location = useLocation();

  const { widthDimension, widthDimensionPx } = ganttDimensions;

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (block?.startMoment) {
      setCalendarDay(block.startMoment.day());
    }
  }, [block, dateReference, viewDimensions]);

  useEffect(() => {
    const { startHour, endHour, workingDays } = viewDimensions;
    const { day, month, year } = dateReference;

    if (
      startHour !== null &&
      endHour !== null &&
      widthDimension &&
      workingDays &&
      calendarDay !== null
    ) {
      // get first day of week to be referenced as startpoint
      let dateRef = moment()
        .year(year)
        .month(month)
        .date(day)
        .set({ hour: startHour, minute: 0, second: 0 });
      if (view?.id === viewIds.month) {
        dateRef = dateRef.date(1);
      } else if (!dayViews.includes(view?.id)) {
        dateRef = dateRef.startOf('week').set({ hour: startHour, minute: 0, second: 0 });
      }
      // last day in calendar
      let endDateRef = null;
      if (view?.id === viewIds.oneDay) {
        endDateRef = dateRef.clone().set({ hour: endHour, minute: 0, second: 0 });
      }
      if (view?.id === viewIds.twoDays) {
        endDateRef = dateRef.clone().add(2, 'day');
      }
      if (view?.id === viewIds.oneWeek) {
        endDateRef = dateRef.clone().add(7, 'day');
      }
      if (view?.id === viewIds.twoWeeks) {
        endDateRef = dateRef.clone().add(14, 'day');
      }
      if (view?.id === viewIds.month) {
        endDateRef = dateRef.clone().add(31, 'day');
      }
      if (view?.id === viewIds.oneWorkWeek) {
        endDateRef = dateRef.clone().add(5, 'day');
      }
      if (view?.id === viewIds.twoWorkWeeks) {
        endDateRef = dateRef.clone().add(12, 'day');
      }

      // calculate how many days one block covers
      const itemBlockDimensions = getItemBlockDimensions(
        block,
        startHour,
        endHour,
        dateRef,
        endDateRef,
        view?.parameters?.widthDayBreaker > 0,
        widthDimension,
        view,
      );

      // loop over days and set blocks
      const items = [];
      itemBlockDimensions.forEach((i) => {
        // get duration of day
        const dimensionsWidth = i.duration * widthDimension;
        const dimensionsLeft = i.left * widthDimension;
        const travelToWidth = i.travelToDuration * widthDimension;
        const travelFromWidth = i.travelFromDuration * widthDimension;
        const labourWidth = i.labourDuration * widthDimension;
        const nextWeekOffset = i.nextWeekOffset * widthDimension;
        // only push items that are in the viewport
        // and if there is a day defined, check if the day must be rendered
        if (
          dimensionsLeft >= 0 &&
          dimensionsLeft <= 99.9
        ) {
          items.push({
            ...block,
            day: i.day,
            travelToWidth,
            travelFromWidth,
            dimensionsWidth,
            dimensionsLeft,
            labourWidth,
            nextWeekOffset,
            nextWeekOffsetPx: i.nextWeekOffset * widthDimensionPx,
            isNextWeek: i.isNextWeek,
            arrows: i.arrows,
            leftMinutes: i.left,
            durationMinutes: i.duration,
            widthDayBreaker: view?.parameters?.widthDayBreaker,
            isLast: i.isLast,
          });
        }
      });
      setItemBlocks(items);
    }
  }, [block, widthDimension, viewDimensions, calendarDay, view, dateReference]);

  const getAdjacentAreaWidth = (b) => {
    const widthInMinutes = 30;
    let blockDuration = b.durationLabourInMinutes;
    if (b.type === 'absence' || b.type === 'reservation' || b.type === 'placeholder') {
      blockDuration = b.durationMinutes;
    }
    return (blockDuration < widthInMinutes ?
      blockDuration :
      widthInMinutes
    ) * widthDimensionPx;
  };

  return (
    <Draggable
      data={block}
      type={block?.type === blockTypes.planning ? ItemTypes.BLOCK : ItemTypes.NON_PLANNING_BLOCK}
    >
      <Wrapper>
        {itemBlocks &&
          itemBlocks?.map((itemBlock, day) =>
            <ItemBlockWrapper
              key={day}
              width={itemBlock?.dimensionsWidth}
              left={itemBlock?.dimensionsLeft}
              day={itemBlock.day}
              top={top}
              isNextWeek={itemBlock?.isNextWeek}
              nextWeekOffset={itemBlock?.nextWeekOffset}
              widthDayBreaker={view?.parameters?.widthDayBreaker}
              id={block.id}
            >
              {/* Only add adjacent area when block is the last of his subset and
               the id of the dragged item is not equal to the id of the block.
               That way a block can't go adjacent to itself */}
              {itemBlock?.isLast &&
                item?.data?.id !== itemBlock?.id &&
                isDraggingOver &&
                itemBlock?.type === blockTypes.planning &&
                <AdjacentArea
                  className='adjacent-area'
                  width={getAdjacentAreaWidth(itemBlock)}
                  right={itemBlock?.travelFromWidth &&
                    itemBlock?.durationTravelFromInMinutes * widthDimensionPx
                  }
                  onDragEnter={() => !adjacentBlock && dispatch(setAdjacentBlock(itemBlock))}
                  onDragLeave={() => adjacentBlock && dispatch(setAdjacentBlock(null))}
                />}
              <Block
                data={itemBlock}
                publishToSAP={publishToSAP}
                splitBlock={splitBlock}
                markDateConfirmed={markDateConfirmed}
                isDoingAction={isDoingAction}
                isDraggingOver={isDraggingOver}
                deletePlanning={deletePlanning}
                fetchBlockData={fetchBlockData}
                fetchBlockDataSuccess={fetchBlockDataSuccess}
                updateBlockId={updateBlockId}
                updateBlockOperationInfo={updateBlockOperationInfo}
                markDateUnconfirmed={markDateUnconfirmed}
                restoreBackup={restoreBackup}
                recalculateTravelTime={recalculateTravelTime}
                planningAndViewToOpen={planningAndViewToOpen}
                isHighlighted={
                  location?.pathname &&
                  decodeURIComponent(location.pathname).includes(itemBlock?.id)
                }
              />
            </ItemBlockWrapper>,
          )}
      </Wrapper>
    </Draggable>
  );
};

BlockWrapper.propTypes = {
  block: PropTypes.array,
  top: PropTypes.number,
  ganttDimensions: PropTypes.object,
  viewDimensions: PropTypes.object,
  dateReference: PropTypes.object,
  view: PropTypes.object,
  splitBlock: PropTypes.func,
  publishToSAP: PropTypes.func,
  markDateConfirmed: PropTypes.func,
  isDoingAction: PropTypes.bool,
  isDraggingOver: PropTypes.bool,
  deletePlanning: PropTypes.func,
  fetchBlockData: PropTypes.func,
  fetchBlockDataSuccess: PropTypes.func,
  updateBlockId: PropTypes.func,
  updateBlockOperationInfo: PropTypes.func,
  markDateUnconfirmed: PropTypes.func,
  restoreBackup: PropTypes.func,
  recalculateTravelTime: PropTypes.func,
  planningAndViewToOpen: PropTypes.object,
};

export default BlockWrapper;
